import classNames from 'classnames';
import React, { useCallback, useEffect } from 'react';
import { AreaChart } from 'components/AreaChart';
import styles from './styles.module.scss';
import moment from 'moment';
import { ArrowDown, ArrowUp } from './icons';
import { getAddressFormattedCassAddress } from 'helpers/cass';
import { useHistory } from 'react-router-dom';
import { groupBy, meanBy } from 'lodash-es';
import { priceConverter } from 'helpers';

type Props = {
  address: any;
  wrapperClassname?: string;
  valuationData: any;
};

export const AddressDetails: React.FC<Props> = ({ address, wrapperClassname, valuationData }) => {
  const history = useHistory();

  const [addressValuationData, setAddressValuationData] = React.useState<any>(null);
  const [rentalValuationData, setRentalValuationData] = React.useState<any>(null);
  const isNotRecentAddress =
    address?.CreatedDate && moment().diff(moment(address?.CreatedDate), 'minutes') > 5;
  const { address1, address2 } = getAddressFormattedCassAddress(address?.address);

  useEffect(() => {
    if (!valuationData?.addressValuations?.valuations?.length) return;

    const valuations = valuationData.addressValuations.valuations;

    const groupAndAverageByMonth = (valueKey) => {
      const entries = valuations
        .filter((v) => v?.[valueKey] != null && v?.month && v?.year)
        .map((v) => {
          const numericValue = parseFloat(v[valueKey]);
          if (isNaN(numericValue)) return null;

          const monthKey = moment({ year: v.year, month: v.month - 1 }).format('YYYY-MM');

          return {
            value: numericValue,
            month: monthKey,
          };
        })
        .filter(Boolean);

      const grouped = groupBy(entries, 'month');

      const averaged = Object.entries(grouped)
        .map(([month, values]) => ({
          x: month,
          y: meanBy(values, 'value'),
        }))
        .sort((a, b) => moment(a.x).toDate().getTime() - moment(b.x).toDate().getTime());

      if (averaged.length === 1) {
        const original = averaged[0];
        const duplicated = {
          x: moment(original.x).add(1, 'month').format('YYYY-MM'),
          y: original.y,
        };
        averaged.push(duplicated);
      }

      return averaged;
    };

    const addressData = [
      {
        name: 'Average Address Valuation',
        data: groupAndAverageByMonth('homeValue'),
      },
    ];

    const rentalData = [
      {
        name: 'Average Rental Valuation',
        data: groupAndAverageByMonth('rentalValue'),
      },
    ];

    setAddressValuationData(addressData);
    setRentalValuationData(rentalData);
  }, [valuationData]);

  const AreaChartOptions = {
    xaxis: {
      labels: {
        show: false,
      },
    },
  };

  const openAddress = useCallback(() => {
    history.push('/intel', {
      address1Param: address1,
      address2Param: address2,
      addressIdParam: address?.Id,
    });
  }, [address?.address]);

  const renderPlaceHolder = () => {
    return isNotRecentAddress ? (
      <div>-</div>
    ) : (
      <div>
        <div className={styles.placeholderContainer}>
          <div className={classNames(styles.placeholderText)}>Calculating...</div>
          <div className={styles.whiteOverlay} />
        </div>
        <div className={styles.placeholderLine} />
      </div>
    );
  };

  function getLastCalculatedMonthYear(data) {
    const attomStats = data?.attom || {};
    const housecanaryStats = data?.houseCanary || {};

    // Compare year and month
    if (attomStats?.year || 0 > housecanaryStats?.year || 0) return attomStats;
    if (attomStats?.year || 0 < housecanaryStats?.year || 0) return housecanaryStats;
    if (attomStats?.month || 0 >= housecanaryStats?.month || 0) return attomStats;
    return housecanaryStats;
  }

  const { month: lastCalculatedMonth, year: lastCalculatedYear } = getLastCalculatedMonthYear(
    valuationData?.addressValuations,
  );

  const getRange = (data, key) => {
    const isValidAttom =
      lastCalculatedMonth === data?.attom?.month && lastCalculatedYear === data?.attom?.year;
    const isValidHouseCanary =
      lastCalculatedMonth === data?.houseCanary?.month &&
      lastCalculatedYear === data?.houseCanary?.year;

    const manualValues = {
      homeValue: 'currentManualHomeValue',
      rentalValue: 'currentManualRentalValue',
    };

    const values = [
      ...(isValidAttom ? [data?.attom?.[key], data?.attom?.[manualValues?.[key]]] : []),
      ...(isValidHouseCanary
        ? [data?.houseCanary?.[key], data?.houseCanary?.[manualValues?.[key]]]
        : []),
    ]
      .filter(Boolean)
      .map((v) => Number(v));

    if (values.length > 1) {
      const min = Math.min(...values);
      const max = Math.max(...values);

      const formattedMin = priceConverter(min);
      const formattedMax = priceConverter(max);

      return min === max ? formattedMin : `${formattedMin} - ${formattedMax}`;
    } else if (data?.attom?.[key] && isValidAttom) {
      return Number(data?.attom?.[key])?.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
      });
    } else if (data?.houseCanary?.[key] && isValidHouseCanary) {
      return Number(data?.houseCanary?.[key])?.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
      });
    } else {
      return '-';
    }
  };

  return (
    <div className={classNames(styles.addressWrapper, wrapperClassname)}>
      <div className={styles.header} onClick={openAddress}>
        <div className={styles.title}>{address1}</div>
        <div className={styles.subtitle}>{address2}</div>
      </div>
      <div className={styles.content}>
        <div className={styles.plotContainer}>
          <>
            <div className={styles.title}>Estimated Home Value</div>
            <div className={classNames(styles.subtitle, styles.purple)}>
              {[
                valuationData?.addressValuations?.attom?.homeValue,
                valuationData?.addressValuations?.attom?.currentManualHomeValue,
                valuationData?.addressValuations?.houseCanary?.homeValue,
                valuationData?.addressValuations?.houseCanary?.currentManualHomeValue,
              ]?.filter(Boolean)?.length
                ? getRange(valuationData?.addressValuations, 'homeValue')
                : renderPlaceHolder()}

              {valuationData?.addressValuations?.homeValue &&
              valuationData?.addressValuations?.homeValue !== 0 &&
              addressValuationData?.length >= 2 ? (
                <span
                  className={
                    !valuationData?.addressValuations.homeValueUp &&
                    valuationData?.addressValuations.homePercent
                      ? styles.decrease
                      : styles.increase
                  }
                >
                  <div>
                    {!valuationData?.addressValuations?.homeValueUp &&
                    valuationData?.addressValuations?.homePercent ? (
                      <ArrowDown />
                    ) : (
                      <ArrowUp />
                    )}
                    {valuationData?.addressValuations?.homePercent
                      ? parseFloat(valuationData?.addressValuations?.homePercent).toFixed(2)
                      : 0}
                    %
                  </div>
                </span>
              ) : null}
            </div>

            {addressValuationData && (
              <AreaChart
                curve="smooth"
                height={70}
                chartSeries={addressValuationData}
                options={AreaChartOptions}
                colors={['#827cce', '#6cd5f1']}
              />
            )}
          </>
        </div>
        <div className={styles.plotContainer}>
          <>
            <div className={styles.title}>Estimated Rental Value</div>
            <div className={classNames(styles.subtitle, styles.aqua)}>
              {[
                valuationData?.addressValuations?.attom?.rentalValue,
                valuationData?.addressValuations?.attom?.currentManualRentalValue,
                valuationData?.addressValuations?.houseCanary?.rentalValue,
                valuationData?.addressValuations?.houseCanary?.currentManualRentalValue,
              ]?.filter(Boolean)?.length
                ? getRange(valuationData?.addressValuations, 'rentalValue')
                : renderPlaceHolder()}

              {valuationData?.addressValuations?.rentalValue &&
              valuationData?.addressValuations?.rentalValue !== 0 &&
              rentalValuationData?.length >= 2 ? (
                <span
                  className={
                    !valuationData?.addressValuations?.rentalValueUp &&
                    valuationData?.addressValuations?.rentalPercent
                      ? styles.decrease
                      : styles.increase
                  }
                >
                  <div>
                    {!valuationData?.addressValuations.rentalValueUp &&
                    valuationData?.addressValuations.rentalPercent ? (
                      <ArrowDown />
                    ) : (
                      <ArrowUp />
                    )}
                    {valuationData?.addressValuations?.rentalPercent
                      ? parseFloat(valuationData?.addressValuations?.rentalPercent).toFixed(2)
                      : 0}
                    %
                  </div>
                </span>
              ) : null}
            </div>

            {rentalValuationData && (
              <AreaChart
                curve="smooth"
                height={70}
                chartSeries={rentalValuationData}
                options={AreaChartOptions}
                colors={['#6cd5f1', '#827cce']}
              />
            )}
          </>
        </div>
      </div>

      <div className={styles.activityWrapper}>
        <div className={styles.activityCard}>
          <div className={styles.activityCardTitle}>Neighborhood Activity</div>
          <div className={classNames(styles.activityCardContent, styles.orangeTitle)}>
            {valuationData?.neighborhoodActivities?.total !== 0 ||
            valuationData?.addressValuations?.homeValue ||
            valuationData?.addressValuations?.rentalValue ? (
              valuationData?.neighborhoodActivities?.total
            ) : (
              <div className={styles.dots}>...</div>
            )}
          </div>
        </div>
        <div className={styles.activityCard}>
          <div className={styles.activityCardTitle}>Reminders</div>
          <div className={classNames(styles.activityCardContent, styles.redTitle)}>
            {valuationData?.reminders?.length || <div className={styles.dots}>...</div>}
          </div>
        </div>
        <div className={styles.activityCard}>
          <div className={styles.activityCardTitle}>New Intel</div>
          <div className={classNames(styles.activityCardContent, styles.greenTitle)}>
            <div className={styles.dots}>...</div>
          </div>
        </div>
      </div>
    </div>
  );
};
